/**
 * CustomerDetail — Task 27 (ZTNA Customers v2)
 *
 * ResizableDrawer de detalhe de um cliente ZTNA (v2: global + grants por app).
 *
 * Seções:
 *  - Informações básicas (sem ambientes, sem expiração — agora é por grant)
 *  - Ações rápidas: Editar, Reenviar convite, Revogar cliente
 *    (Renovar foi removido do nível customer; agora vive por grant)
 *  - Aplicações autorizadas: lista de GrantCard + botão "Adicionar"
 *    (abre GrantsModal); cada grant tem seus próprios Renovar/Revogar.
 *  - Sessões ativas (tabela + revogar por jti + revogar todas)
 *  - Acessos por aplicação (agregado a partir de audit events)
 *  - Histórico de auditoria
 *
 * Gates: useCan('ztna_customer','revoke'), useCan('ztna_customer','update'),
 *        useCan('ztna_customer','audit_read')
 *
 * Spec §10.2.
 */
import { useState } from 'react'
import {
  Alert,
  Badge,
  Box,
  Button,
  Code,
  Collapse,
  Divider,
  Group,
  Loader,
  Modal,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core'
import { DatePickerInput } from '@mantine/dates'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import {
  IconAlertTriangle,
  IconLock,
  IconCalendar,
  IconChevronDown,
  IconChevronRight,
  IconExternalLink,
  IconMail,
  IconPlus,
  IconRefresh,
  IconShieldOff,
  IconTrash,
  IconX,
} from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'

import { ResizableDrawer } from '@/components/ui/ResizableDrawer'
import { useCan } from '@/store/auth'
import {
  ztnaCustomersApi,
  type CustomerAppGrant,
  type CustomerAuditEvent,
  type ZtnaCustomer,
} from '@/api/ztnaCustomers'
import { BloqueiosAlert } from './BloqueiosAlert'
import { GrantCard } from './GrantCard'
import { GrantsModal } from './GrantsModal'
import { formatApiError } from '@/api/errors'

dayjs.extend(relativeTime)

// ─── Props ─────────────────────────────────────────────────────────────────────

interface Props {
  customer: ZtnaCustomer | null
  opened: boolean
  onClose: () => void
  onEdit?: (customer: ZtnaCustomer) => void
}

// ─── Status helpers ────────────────────────────────────────────────────────────

function statusColor(status: string): string {
  switch (status) {
    case 'active':            return 'teal'
    case 'pending_password':  return 'yellow'
    case 'revoked':           return 'red'
    default:                  return 'gray'
  }
}

function statusLabel(status: string): string {
  switch (status) {
    case 'active':            return 'Ativo'
    case 'pending_password':  return 'Aguardando senha'
    case 'revoked':           return 'Revogado'
    default:                  return status
  }
}

// ─── Audit row with collapsible payload ───────────────────────────────────────

// Maps internal event types to friendly Portuguese labels + accent color.
// Falls back to raw action/event_type when not mapped.
function eventMeta(item: CustomerAuditEvent): { label: string; color: string } {
  const a = item.action ?? ''
  const e = item.event_type ?? ''
  if (a === 'ztna.customer.magic_issued')   return { label: 'Login bem-sucedido',          color: 'teal'   }
  if (a === 'ztna.customer.login_failed')   return { label: 'Tentativa de login falhou',   color: 'orange' }
  if (e === 'ztna.bridge.customer_issued')  return { label: 'Acessou aplicação',            color: 'teal'   }
  if (a === 'invitation_sent')              return { label: 'Convite enviado',              color: 'blue'   }
  if (a === 'created' || e.endsWith('.created'))   return { label: 'Cliente criado',  color: 'blue' }
  if (a === 'updated' || e.endsWith('.updated'))   return { label: 'Cliente atualizado', color: 'gray' }
  if (a === 'revoked' || e.endsWith('.revoked'))   return { label: 'Cliente revogado', color: 'red'  }
  if (a === 'renewed' || e.endsWith('.renewed'))   return { label: 'Acesso renovado',  color: 'teal' }
  if (a === 'grant_added'   || e.endsWith('.grant_added'))   return { label: 'Aplicação liberada',  color: 'teal' }
  if (a === 'grant_revoked' || e.endsWith('.grant_revoked')) return { label: 'Aplicação revogada',  color: 'red'  }
  if (a === 'grant_renewed' || e.endsWith('.grant_renewed')) return { label: 'Grant renovado',      color: 'teal' }
  return { label: a || e || 'evento', color: 'blue' }
}

function AuditRow({ item }: { item: CustomerAuditEvent }) {
  const [open, setOpen] = useState(false)
  const hasPayload =
    item.payload != null &&
    typeof item.payload === 'object' &&
    Object.keys(item.payload).length > 0

  const { label, color } = eventMeta(item)

  // Extract inline metadata from payload (best-effort — never assume shape)
  const payload = (item.payload ?? {}) as Record<string, unknown>
  const appSlug =
    typeof payload.app_slug === 'string' ? payload.app_slug :
    typeof payload.slug === 'string'     ? payload.slug     : null
  const sourceIp =
    typeof payload.source_ip === 'string' ? payload.source_ip :
    typeof payload.ip === 'string'        ? payload.ip        : null
  const env =
    typeof payload.environment === 'string' ? payload.environment : null

  return (
    <Box
      py={6}
      px={4}
      style={{
        borderLeft: `2px solid var(--mantine-color-${color}-3)`,
        cursor: hasPayload ? 'pointer' : 'default',
      }}
      onClick={hasPayload ? () => setOpen(v => !v) : undefined}
    >
      <Group gap={8} wrap="nowrap" align="flex-start">
        {hasPayload ? (
          open
            ? <IconChevronDown size={14} style={{ flexShrink: 0, marginTop: 3 }} />
            : <IconChevronRight size={14} style={{ flexShrink: 0, marginTop: 3 }} />
        ) : (
          <Box w={14} style={{ flexShrink: 0 }} />
        )}
        <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
          <Group gap={6} wrap="nowrap">
            <Badge size="xs" variant="light" color={color}>{label}</Badge>
            {appSlug && (
              <Badge size="xs" variant="outline" color="gray" ff="monospace">
                {appSlug}{env ? `·${env}` : ''}
              </Badge>
            )}
          </Group>
          <Group gap={6} wrap="nowrap">
            <Text
              size="xs"
              c="dimmed"
              title={dayjs(item.created_at).format('YYYY-MM-DD HH:mm:ss')}
            >
              {dayjs(item.created_at).format('YYYY-MM-DD HH:mm')} ({dayjs(item.created_at).fromNow()})
            </Text>
            {sourceIp && (
              <>
                <Text size="xs" c="dimmed">·</Text>
                <Text size="xs" c="dimmed" ff="monospace">IP {sourceIp}</Text>
              </>
            )}
            {item.actor_email && (
              <>
                <Text size="xs" c="dimmed">·</Text>
                <Text size="xs" c="dimmed" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  por {item.actor_email}
                </Text>
              </>
            )}
          </Group>
          {hasPayload && (
            <Collapse in={open}>
              <Code block style={{ fontSize: 11, maxHeight: 200, overflow: 'auto' }}>
                {JSON.stringify(item.payload, null, 2)}
              </Code>
            </Collapse>
          )}
        </Stack>
      </Group>
    </Box>
  )
}

// ─── Main component ────────────────────────────────────────────────────────────

export function CustomerDetail({ customer, opened, onClose, onEdit }: Props) {
  const can          = useCan()
  const queryClient  = useQueryClient()
  const canRevoke    = can('ztna_customer', 'revoke')
  const canUpdate    = can('ztna_customer', 'update')
  const canAudit     = can('ztna_customer', 'audit_read')

  // ── Add grants modal state ────────────────────────────────────────────────────
  const [addGrantsOpen, setAddGrantsOpen] = useState(false)

  // ── Renew grant state ─────────────────────────────────────────────────────────
  const [renewGrant, setRenewGrant] = useState<CustomerAppGrant | null>(null)
  const [renewGrantDate, setRenewGrantDate] = useState<Date | null>(null)
  const maxRenewDate = dayjs().add(365, 'day').toDate()

  // ── Sessions query ─────────────────────────────────────────────────────────────
  const sessionsQuery = useQuery({
    queryKey: ['ztna', 'customer-sessions', customer?.id],
    queryFn: () => ztnaCustomersApi.listSessions(customer!.id),
    enabled: customer != null && opened,
    staleTime: 30_000,
  })

  // ── Bloqueios de login (por que não entra, e quanto falta) ─────────────────────
  const bloqueiosQuery = useQuery({
    queryKey: ['ztna', 'customer-bloqueios', customer?.id],
    queryFn: () => ztnaCustomersApi.getCustomerBloqueios(customer!.id),
    enabled: customer != null && opened,
    staleTime: 30_000,
  })
  const bloqueios = bloqueiosQuery.data ?? []

  // ── Audit query ────────────────────────────────────────────────────────────────
  const auditQuery = useQuery({
    queryKey: ['ztna', 'customer-audit', customer?.id],
    queryFn: () => ztnaCustomersApi.getAudit(customer!.id),
    enabled: customer != null && opened && canAudit,
    staleTime: 30_000,
  })

  // ── Mutations ──────────────────────────────────────────────────────────────────

  const revokeSessionMutation = useMutation({
    mutationFn: (jti: string) => ztnaCustomersApi.revokeSession(customer!.id, jti),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ztna', 'customer-sessions', customer!.id] })
      notifications.show({ title: 'Sessão revogada', message: 'Sessão invalidada com sucesso.', color: 'yellow' })
    },
    onError: (err: Error) => {
      notifications.show({ title: 'Erro ao revogar sessão', message: err.message, color: 'red' })
    },
  })

  const revokeAllSessionsMutation = useMutation({
    mutationFn: async () => {
      const sessions = sessionsQuery.data ?? []
      const active   = sessions.filter(s => !s.revoked)
      await Promise.all(active.map(s => ztnaCustomersApi.revokeSession(customer!.id, s.jti)))
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ztna', 'customer-sessions', customer!.id] })
      notifications.show({ title: 'Todas as sessões revogadas', message: 'Sessões ativas foram invalidadas.', color: 'yellow' })
    },
    onError: (err: Error) => {
      notifications.show({ title: 'Erro ao revogar sessões', message: err.message, color: 'red' })
    },
  })

  const revokeCustomerMutation = useMutation({
    mutationFn: (reason: string) => ztnaCustomersApi.revokeCustomer(customer!.id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ztna', 'customers'] })
      notifications.show({ title: 'Cliente revogado', message: 'Acesso do cliente foi revogado.', color: 'red' })
      onClose()
    },
    onError: (err: unknown) => {
      notifications.show({
        title: 'Erro ao revogar cliente',
        message: formatApiError(err, 'Não foi possível revogar o cliente.'),
        color: 'red',
      })
    },
  })

  const reactivateCustomerMutation = useMutation({
    mutationFn: () => ztnaCustomersApi.reactivateCustomer(customer!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ztna', 'customers'] })
      queryClient.invalidateQueries({ queryKey: ['ztna', 'customer', customer!.id] })
      notifications.show({
        title: 'Cliente reativado',
        message: 'Cliente voltou ao status ativo. Adicione aplicações conforme necessário.',
        color: 'teal',
      })
    },
    onError: (err: unknown) => {
      notifications.show({
        title: 'Erro ao reativar cliente',
        message: formatApiError(err, 'Não foi possível reativar o cliente.'),
        color: 'red',
      })
    },
  })

  const deleteCustomerMutation = useMutation({
    mutationFn: () => ztnaCustomersApi.deleteCustomer(customer!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ztna', 'customers'] })
      notifications.show({
        title: 'Cliente excluído',
        message: 'Cliente removido permanentemente do sistema.',
        color: 'red',
      })
      onClose()
    },
    onError: (err: unknown) => {
      notifications.show({
        title: 'Erro ao excluir cliente',
        message: formatApiError(err, 'Não foi possível excluir o cliente.'),
        color: 'red',
      })
    },
  })

  const unlockCustomerMutation = useMutation({
    mutationFn: () => ztnaCustomersApi.unlockCustomer(customer!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ztna', 'customers'] })
      queryClient.invalidateQueries({ queryKey: ['ztna', 'customer', customer!.id] })
      queryClient.invalidateQueries({ queryKey: ['ztna', 'customer-bloqueios', customer!.id] })
      notifications.show({
        title: 'Cliente desbloqueado',
        message: 'Senha errada, cota de links por e-mail e tentativas por IP liberadas. Cliente pode tentar entrar novamente.',
        color: 'teal',
      })
    },
    onError: (err: unknown) => {
      notifications.show({
        title: 'Erro ao desbloquear cliente',
        message: formatApiError(err, 'Não foi possível desbloquear o cliente.'),
        color: 'red',
      })
    },
  })

  const renewGrantMutation = useMutation({
    mutationFn: ({ grantId, expiresAt }: { grantId: number; expiresAt: string }) =>
      ztnaCustomersApi.renewGrant(customer!.id, grantId, expiresAt),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ztna', 'customers'] })
      queryClient.invalidateQueries({ queryKey: ['ztna', 'customer', customer!.id] })
      queryClient.invalidateQueries({ queryKey: ['ztna', 'customer-audit', customer!.id] })
      notifications.show({ title: 'Acesso renovado', color: 'teal', message: 'Data atualizada.' })
      setRenewGrant(null)
      setRenewGrantDate(null)
    },
    onError: (err: unknown) => {
      notifications.show({
        title: 'Erro ao renovar',
        message: formatApiError(err, 'Não foi possível renovar o acesso.'),
        color: 'red',
      })
    },
  })

  const revokeGrantMutation = useMutation({
    mutationFn: ({ grantId, reason }: { grantId: number; reason: string }) =>
      ztnaCustomersApi.revokeGrant(customer!.id, grantId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ztna', 'customers'] })
      queryClient.invalidateQueries({ queryKey: ['ztna', 'customer', customer!.id] })
      queryClient.invalidateQueries({ queryKey: ['ztna', 'customer-audit', customer!.id] })
      notifications.show({
        title: 'Acesso revogado',
        color: 'yellow',
        message: 'Cliente perdeu acesso a essa aplicação.',
      })
    },
    onError: (err: unknown) => {
      notifications.show({
        title: 'Erro ao revogar acesso',
        message: formatApiError(err, 'Não foi possível revogar o acesso.'),
        color: 'red',
      })
    },
  })

  const reactivateGrantMutation = useMutation({
    mutationFn: (grantId: number) =>
      ztnaCustomersApi.reactivateGrant(customer!.id, grantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ztna', 'customers'] })
      queryClient.invalidateQueries({ queryKey: ['ztna', 'customer', customer!.id] })
      queryClient.invalidateQueries({ queryKey: ['ztna', 'customer-audit', customer!.id] })
      notifications.show({
        title: 'Acesso reativado',
        color: 'teal',
        message: 'Cliente recuperou acesso a essa aplicação.',
      })
    },
    onError: (err: Error & { response?: { data?: { detail?: string } } }) => {
      const detail = err.response?.data?.detail
      // `detail` aqui e' CODIGO, nao texto: escolhe a frase. So o fallback muda.
      let msg = formatApiError(err, 'Não foi possível reativar o acesso.')
      if (detail === 'grant_already_active') {
        msg = 'Já existe um acesso ativo para esta aplicação. Use Renovar ou Revogar nele.'
      } else if (detail === 'grant_not_revoked') {
        msg = 'Este acesso já está ativo.'
      } else if (typeof detail === 'string') {
        msg = detail
      }
      notifications.show({ title: 'Erro ao reativar acesso', message: msg, color: 'red' })
    },
  })

  const deleteGrantMutation = useMutation({
    mutationFn: (grantId: number) =>
      ztnaCustomersApi.deleteGrant(customer!.id, grantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ztna', 'customers'] })
      queryClient.invalidateQueries({ queryKey: ['ztna', 'customer', customer!.id] })
      queryClient.invalidateQueries({ queryKey: ['ztna', 'customer-audit', customer!.id] })
      notifications.show({
        title: 'Acesso excluído',
        color: 'gray',
        message: 'Aplicação removida do cliente.',
      })
    },
    onError: (err: unknown) => {
      notifications.show({
        title: 'Erro ao excluir acesso',
        message: formatApiError(err, 'Não foi possível excluir o acesso.'),
        color: 'red',
      })
    },
  })

  const resendMutation = useMutation({
    mutationFn: () => ztnaCustomersApi.resendInvitation(customer!.id),
    onSuccess: () => {
      notifications.show({ title: 'Convite reenviado', message: 'E-mail enviado com sucesso.', color: 'teal' })
    },
    onError: (err: unknown) => {
      notifications.show({
        title: 'Erro ao reenviar convite',
        message: formatApiError(err, 'Não foi possível reenviar o convite.'),
        color: 'red',
      })
    },
  })

  // ── Open revoke-customer modal ─────────────────────────────────────────────────

  function openRevokeModal() {
    let reason = ''
    modals.openConfirmModal({
      title: 'Revogar acesso do cliente',
      children: (
        <Stack gap="sm">
          <Alert color="red" variant="light" icon={<IconShieldOff size={16} />}>
            Esta ação revoga imediatamente todo acesso do cliente. As sessões ativas
            serão invalidadas.
          </Alert>
          <Textarea
            label="Motivo (obrigatório)"
            placeholder="Informe o motivo da revogação…"
            autosize
            minRows={2}
            withAsterisk
            onChange={e => { reason = e.currentTarget.value }}
          />
        </Stack>
      ),
      labels: { confirm: 'Revogar acesso', cancel: 'Cancelar' },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        if (!reason.trim()) {
          notifications.show({ title: 'Motivo obrigatório', message: 'Informe o motivo da revogação antes de continuar.', color: 'orange' })
          return
        }
        revokeCustomerMutation.mutate(reason.trim())
      },
    })
  }

  // ── Open delete-customer modal (hard delete, permanente) ─────────────────────

  function openDeleteCustomerModal() {
    let typed = ''
    const expected = customer?.email ?? ''
    modals.openConfirmModal({
      title: 'Excluir cliente permanentemente?',
      children: (
        <Stack gap="sm">
          <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
            Esta ação <strong>não pode ser desfeita</strong>. O cliente será
            removido do banco junto com todos os grants, sessões, convites e
            magic links. O histórico de auditoria é preservado.
          </Alert>
          <Text size="sm">
            Para confirmar, digite o e-mail do cliente exatamente como aparece:
          </Text>
          <Text size="sm" ff="monospace" fw={600} c="dimmed">
            {expected}
          </Text>
          <TextInput
            placeholder="Digite o e-mail"
            onChange={e => { typed = e.currentTarget.value.trim() }}
            data-testid="delete-customer-confirm-email"
            autoFocus
          />
        </Stack>
      ),
      labels: { confirm: 'Excluir permanentemente', cancel: 'Cancelar' },
      confirmProps: { color: 'red', leftSection: <IconTrash size={14} /> },
      onConfirm: () => {
        if (typed.toLowerCase() !== expected.toLowerCase()) {
          notifications.show({
            title: 'E-mail não confere',
            message: 'Digite o e-mail exatamente como aparece para confirmar.',
            color: 'orange',
          })
          return
        }
        deleteCustomerMutation.mutate()
      },
    })
  }

  // ── Open revoke-grant modal ────────────────────────────────────────────────────

  function openRevokeGrantModal(grant: CustomerAppGrant) {
    let reason = ''
    modals.openConfirmModal({
      title: `Revogar acesso a ${grant.application.display_name}?`,
      children: (
        <Stack gap="sm">
          <Text size="sm">
            Cliente perde acesso a esta aplicação imediatamente. Sessões ativas
            continuam para outras apps.
          </Text>
          <Textarea
            label="Motivo (obrigatório)"
            placeholder="Informe o motivo…"
            autosize
            minRows={2}
            withAsterisk
            onChange={e => { reason = e.currentTarget.value }}
          />
        </Stack>
      ),
      labels: { confirm: 'Revogar', cancel: 'Cancelar' },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        if (!reason.trim()) {
          notifications.show({ title: 'Motivo obrigatório', message: 'Informe o motivo.', color: 'orange' })
          return
        }
        revokeGrantMutation.mutate({ grantId: grant.id, reason: reason.trim() })
      },
    })
  }

  // ── Open delete-grant modal ───────────────────────────────────────────────────

  function openDeleteGrantModal(grant: CustomerAppGrant) {
    modals.openConfirmModal({
      title: `Excluir acesso a ${grant.application.display_name}?`,
      children: (
        <Stack gap="sm">
          <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
            Esta ação é <strong>permanente</strong> — diferente de Revogar,
            o registro deste acesso é removido do cliente. O histórico de
            auditoria do grant continua existindo, mas a aplicação some da
            lista do cliente.
          </Alert>
          <Text size="sm" c="dimmed" ff="monospace">{grant.application.hostname}</Text>
        </Stack>
      ),
      labels: { confirm: 'Excluir permanentemente', cancel: 'Cancelar' },
      confirmProps: { color: 'red' },
      onConfirm: () => deleteGrantMutation.mutate(grant.id),
    })
  }

  // ── Open renew-grant modal ────────────────────────────────────────────────────

  function openRenewGrantModal(grant: CustomerAppGrant) {
    setRenewGrant(grant)
    setRenewGrantDate(dayjs(grant.expires_at).toDate())
  }

  function closeRenewGrantModal() {
    setRenewGrant(null)
    setRenewGrantDate(null)
  }

  if (!customer) return null

  const sessions    = sessionsQuery.data ?? []
  const activeSessions = sessions.filter(s => !s.revoked)
  const auditItems  = auditQuery.data?.items ?? []

  // ── Grants: ativos primeiro, depois revogados (mantém estabilidade no ID) ────
  const sortedGrants = [...customer.grants].sort((a, b) => {
    const aRev = a.revoked_at ? 1 : 0
    const bRev = b.revoked_at ? 1 : 0
    if (aRev !== bRev) return aRev - bRev
    return a.id - b.id
  })
  const activeGrantsCount = customer.grants.filter(g => !g.revoked_at).length

  // Agrega audit events de acesso por app_slug (atende: "quantas vezes acessou, quando, IP")
  // Eventos contemplados: ztna.customer.magic_issued (login) + ztna.bridge.customer_issued (acesso a app)
  const accessByApp = (() => {
    const map = new Map<string, { count: number; lastAt: string; ips: Set<string> }>()
    for (const ev of auditItems) {
      const a = ev.action ?? ''
      const e = ev.event_type ?? ''
      const isAccess = a === 'ztna.customer.magic_issued' || e === 'ztna.bridge.customer_issued'
      if (!isAccess) continue
      const payload = (ev.payload ?? {}) as Record<string, unknown>
      const slug =
        (typeof payload.app_slug === 'string' && payload.app_slug) ||
        (typeof payload.slug     === 'string' && payload.slug)     ||
        '—'
      const ip =
        typeof payload.source_ip === 'string' ? payload.source_ip :
        typeof payload.ip        === 'string' ? payload.ip        : null
      const entry = map.get(slug) ?? { count: 0, lastAt: ev.created_at, ips: new Set<string>() }
      entry.count += 1
      if (dayjs(ev.created_at).isAfter(entry.lastAt)) entry.lastAt = ev.created_at
      if (ip) entry.ips.add(ip)
      map.set(slug, entry)
    }
    return Array.from(map.entries()).sort(
      (a, b) => dayjs(b[1].lastAt).valueOf() - dayjs(a[1].lastAt).valueOf(),
    )
  })()

  return (
    <ResizableDrawer
      storageKey="ztna-customer-detail-width"
      defaultWidth={560}
      minWidth={420}
      opened={opened}
      onClose={onClose}
      position="right"
      withCloseButton
      title={
        <Group gap="xs">
          <Title order={4}>{customer.full_name}</Title>
          <Badge size="sm" color={statusColor(customer.status)} variant="light">
            {statusLabel(customer.status)}
          </Badge>
          {customer.email_bounced && (
            <Tooltip label="E-mail com falha de entrega">
              <Badge size="xs" color="red" variant="filled" leftSection={<IconAlertTriangle size={10} />}>
                e-mail com falha
              </Badge>
            </Tooltip>
          )}
        </Group>
      }
    >
      <Stack gap="md">

        {/* Info básica (sem ambientes, sem expiração — agora é por grant) */}
        <section>
          <Stack gap={4}>
            <Group gap={6}>
              <Text size="xs" c="dimmed" w={90}>E-mail</Text>
              <Text size="sm" ff="monospace">{customer.email}</Text>
            </Group>
            {customer.company && (
              <Group gap={6}>
                <Text size="xs" c="dimmed" w={90}>Empresa</Text>
                <Text size="sm">{customer.company}</Text>
              </Group>
            )}
            {customer.phone && (
              <Group gap={6}>
                <Text size="xs" c="dimmed" w={90}>Telefone</Text>
                <Text size="sm">{customer.phone}</Text>
              </Group>
            )}
          </Stack>
        </section>

        {/* Ações rápidas (sem Renovar global — agora é por grant) */}
        <Group gap="xs" wrap="wrap">
          {canUpdate && (
            <Button
              size="xs"
              variant="default"
              onClick={() => onEdit?.(customer)}
            >
              Editar
            </Button>
          )}

          {customer.status !== 'revoked' && canUpdate && (
            <Tooltip
              label={
                customer.status === 'pending_password'
                  ? 'Reenvia o e-mail de cadastro'
                  : 'Gera novo link de cadastro de senha e envia por e-mail'
              }
            >
              <Button
                size="xs"
                variant="light"
                color="blue"
                leftSection={<IconMail size={14} />}
                loading={resendMutation.isPending}
                onClick={() => resendMutation.mutate()}
              >
                {customer.status === 'pending_password'
                  ? 'Reenviar convite'
                  : 'Reenviar link de cadastro'}
              </Button>
            </Tooltip>
          )}

          {canRevoke && customer.status !== 'revoked' && (
            <Button
              size="xs"
              variant="light"
              color="red"
              leftSection={<IconShieldOff size={14} />}
              loading={revokeCustomerMutation.isPending}
              onClick={openRevokeModal}
            >
              Revogar cliente
            </Button>
          )}

          {canRevoke && customer.status === 'revoked' && (
            <Tooltip label="Volta o cliente ao status ativo. Grants e sessões revogados continuam revogados — adicione novamente conforme necessário.">
              <Button
                size="xs"
                variant="light"
                color="teal"
                leftSection={<IconRefresh size={14} />}
                loading={reactivateCustomerMutation.isPending}
                onClick={() => reactivateCustomerMutation.mutate()}
              >
                Reativar cliente
              </Button>
            </Tooltip>
          )}

          {canRevoke && bloqueios.some((b) => b.desbloqueavel) && (
            <Tooltip label="Libera o bloqueio por senha errada, a cota de links de acesso por e-mail e as tentativas por IP. A cota de redefinição de senha não é liberada.">
              <Button
                size="xs"
                variant="light"
                color="orange"
                leftSection={<IconLock size={14} />}
                loading={unlockCustomerMutation.isPending}
                onClick={() => unlockCustomerMutation.mutate()}
              >
                Desbloquear
              </Button>
            </Tooltip>
          )}

          {canRevoke && (
            <Tooltip
              label="Remove o cliente permanentemente do banco — útil para customers de teste ou criados com dados incorretos. Diferente de Revogar, não fica na lista. Histórico de auditoria é preservado."
              multiline
              w={280}
            >
              <Button
                size="xs"
                variant="subtle"
                color="red"
                leftSection={<IconTrash size={14} />}
                loading={deleteCustomerMutation.isPending}
                onClick={openDeleteCustomerModal}
              >
                Excluir
              </Button>
            </Tooltip>
          )}
        </Group>

        {/* Bloqueios de login ativos, com motivo e contagem regressiva */}
        <BloqueiosAlert bloqueios={bloqueios} onExpirou={bloqueiosQuery.refetch} />

        {/* Auth state info (visibility — Task 117) */}
        {(customer.password_set_at || customer.last_login_at) && (
          <Group gap={20} mt={4}>
            {customer.password_set_at && (
              <Text size="xs" c="dimmed">
                Senha definida: <span style={{ fontFamily: 'monospace' }}>{dayjs(customer.password_set_at).format('YYYY-MM-DD HH:mm')}</span>
              </Text>
            )}
            {customer.last_login_at && (
              <Text size="xs" c="dimmed">
                Último login: <span style={{ fontFamily: 'monospace' }}>{dayjs(customer.last_login_at).fromNow()}</span>
              </Text>
            )}
          </Group>
        )}

        <Divider />

        {/* Aplicações autorizadas (NOVA — Task 27) */}
        <section>
          <Group justify="space-between" mb="xs">
            <Title order={6} c="var(--ai-brand-azul)">
              Aplicações autorizadas ({activeGrantsCount})
            </Title>
            {canUpdate && customer.status !== 'revoked' && (
              <Button
                size="xs"
                variant="light"
                color="teal"
                leftSection={<IconPlus size={14} />}
                onClick={() => setAddGrantsOpen(true)}
              >
                Adicionar
              </Button>
            )}
          </Group>
          {customer.grants.length === 0 ? (
            <Text size="xs" c="dimmed">Sem aplicações autorizadas.</Text>
          ) : (
            <Stack gap="xs">
              {sortedGrants.map(g => (
                <GrantCard
                  key={g.id}
                  grant={g}
                  canRevoke={canRevoke}
                  canRenew={canUpdate}
                  onRenew={openRenewGrantModal}
                  onRevoke={openRevokeGrantModal}
                  onReactivate={(grant) =>
                    reactivateGrantMutation.mutate(grant.id)
                  }
                  onDelete={(grant) => openDeleteGrantModal(grant)}
                />
              ))}
            </Stack>
          )}
        </section>

        <Divider />

        {/* Sessões ativas */}
        <section>
          <Group justify="space-between" mb="xs">
            <Title order={6} c="var(--ai-brand-azul)">
              Sessões ativas ({activeSessions.length})
            </Title>
            {canRevoke && activeSessions.length > 0 && (
              <Button
                size="xs"
                variant="subtle"
                color="red"
                leftSection={<IconX size={12} />}
                loading={revokeAllSessionsMutation.isPending}
                onClick={() =>
                  modals.openConfirmModal({
                    title: 'Revogar todas as sessões?',
                    children: (
                      <Text size="sm">
                        Todas as {activeSessions.length} sessão(ões) ativa(s) serão
                        invalidadas imediatamente.
                      </Text>
                    ),
                    labels: { confirm: 'Revogar todas', cancel: 'Cancelar' },
                    confirmProps: { color: 'red' },
                    onConfirm: () => revokeAllSessionsMutation.mutate(),
                  })
                }
              >
                Revogar todas
              </Button>
            )}
          </Group>

          {sessionsQuery.isLoading ? (
            <Group gap={8}>
              <Loader size="xs" />
              <Text size="xs" c="dimmed">Carregando sessões…</Text>
            </Group>
          ) : sessionsQuery.isError ? (
            <Text size="xs" c="red">Falha ao carregar sessões.</Text>
          ) : sessions.length === 0 ? (
            <Text size="xs" c="dimmed">Nenhuma sessão registrada.</Text>
          ) : (
            <Table striped withTableBorder withColumnBorders>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>JTI</Table.Th>
                  <Table.Th>IP</Table.Th>
                  <Table.Th>Iniciada</Table.Th>
                  <Table.Th>Expira</Table.Th>
                  <Table.Th w={70}></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {sessions.map(s => (
                  <Table.Tr key={s.jti} style={{ opacity: s.revoked ? 0.5 : 1 }}>
                    <Table.Td>
                      <Tooltip label={s.jti}>
                        <Text size="xs" ff="monospace" style={{ maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {s.jti.slice(0, 12)}…
                        </Text>
                      </Tooltip>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" ff="monospace">{s.ip ?? '—'}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Tooltip label={dayjs(s.created_at).format('YYYY-MM-DD HH:mm:ss')}>
                        <Text size="xs">{dayjs(s.created_at).fromNow()}</Text>
                      </Tooltip>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs">{dayjs(s.expires_at).format('YYYY-MM-DD HH:mm')}</Text>
                    </Table.Td>
                    <Table.Td>
                      {s.revoked ? (
                        <Badge size="xs" color="red" variant="light">Revogada</Badge>
                      ) : canRevoke ? (
                        <Button
                          size="xs"
                          variant="subtle"
                          color="red"
                          loading={revokeSessionMutation.isPending}
                          onClick={() =>
                            modals.openConfirmModal({
                              title: 'Revogar sessão?',
                              children: (
                                <Text size="sm">
                                  Sessão <code>{s.jti.slice(0, 12)}…</code> será
                                  invalidada imediatamente.
                                </Text>
                              ),
                              labels: { confirm: 'Revogar', cancel: 'Cancelar' },
                              confirmProps: { color: 'red' },
                              onConfirm: () => revokeSessionMutation.mutate(s.jti),
                            })
                          }
                        >
                          Revogar
                        </Button>
                      ) : null}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </section>

        <Divider />

        {/* Acessos por aplicação (agregado a partir dos audit events) */}
        {canAudit && accessByApp.length > 0 && (
          <>
            <section>
              <Title order={6} c="var(--ai-brand-azul)" mb="xs">
                Acessos por aplicação
              </Title>
              <Table striped withTableBorder withColumnBorders>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Aplicação</Table.Th>
                    <Table.Th ta="right" w={70}>Acessos</Table.Th>
                    <Table.Th>Último acesso</Table.Th>
                    <Table.Th>IPs distintos</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {accessByApp.map(([slug, info]) => (
                    <Table.Tr key={slug}>
                      <Table.Td>
                        <Text size="xs" ff="monospace">{slug}</Text>
                      </Table.Td>
                      <Table.Td ta="right">
                        <Badge size="xs" color="teal" variant="light">{info.count}</Badge>
                      </Table.Td>
                      <Table.Td>
                        <Tooltip label={dayjs(info.lastAt).format('YYYY-MM-DD HH:mm:ss')}>
                          <Text size="xs">{dayjs(info.lastAt).fromNow()}</Text>
                        </Tooltip>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" ff="monospace" c="dimmed">
                          {Array.from(info.ips).join(', ') || '—'}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </section>
            <Divider />
          </>
        )}

        {/* Audit timeline */}
        {canAudit && (
          <section>
            <Group justify="space-between" mb="xs">
              <Title order={6} c="var(--ai-brand-azul)">Histórico de auditoria</Title>
              <Tooltip label="Auditoria global filtrada por este cliente">
                <Text
                  size="xs"
                  c="dimmed"
                  component="a"
                  href={`/auditoria-global?resource_type=ztna_customer&resource_id=${customer.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Group gap={4} wrap="nowrap">
                    <IconExternalLink size={12} />
                    <span>Auditoria global</span>
                  </Group>
                </Text>
              </Tooltip>
            </Group>

            {auditQuery.isLoading ? (
              <Group gap={8}>
                <Loader size="xs" />
                <Text size="xs" c="dimmed">Carregando histórico…</Text>
              </Group>
            ) : auditQuery.isError ? (
              <Text size="xs" c="red">Falha ao carregar auditoria.</Text>
            ) : auditItems.length === 0 ? (
              <Text size="xs" c="dimmed">Nenhum evento registrado.</Text>
            ) : (
              <Stack gap={2}>
                {auditItems.map(item => (
                  <AuditRow key={item.id} item={item} />
                ))}
                {(auditQuery.data?.total ?? 0) > auditItems.length && (
                  <Text size="xs" c="dimmed" ta="center" mt={4}>
                    Mostrando {auditItems.length} de {auditQuery.data?.total} eventos.
                  </Text>
                )}
              </Stack>
            )}
          </section>
        )}

      </Stack>

      {/* Modal: adicionar grants */}
      <GrantsModal
        customer={customer}
        opened={addGrantsOpen}
        onClose={() => setAddGrantsOpen(false)}
      />

      {/* Modal: renovar grant individual */}
      <Modal
        opened={renewGrant != null}
        onClose={closeRenewGrantModal}
        title={
          renewGrant
            ? `Renovar acesso a ${renewGrant.application.display_name}`
            : 'Renovar acesso'
        }
        size="md"
      >
        <Stack gap="sm">
          {renewGrant && (
            <Text size="xs" c="dimmed">
              Expiração atual:{' '}
              <Text span ff="monospace">
                {dayjs(renewGrant.expires_at).format('YYYY-MM-DD')}
              </Text>
            </Text>
          )}
          <DatePickerInput
            label="Nova data de expiração"
            placeholder="Selecione a data"
            value={renewGrantDate}
            onChange={setRenewGrantDate}
            minDate={new Date()}
            maxDate={maxRenewDate}
            leftSection={<IconCalendar size={16} />}
            clearable
            valueFormat="YYYY-MM-DD"
            withAsterisk
          />
          <Group justify="flex-end" gap="xs">
            <Button variant="default" onClick={closeRenewGrantModal}>
              Cancelar
            </Button>
            <Button
              color="teal"
              disabled={!renewGrantDate || !renewGrant}
              loading={renewGrantMutation.isPending}
              leftSection={<IconRefresh size={14} />}
              onClick={() => {
                if (!renewGrant || !renewGrantDate) return
                renewGrantMutation.mutate({
                  grantId: renewGrant.id,
                  expiresAt: dayjs(renewGrantDate).utc().toISOString(),
                })
              }}
            >
              Confirmar renovação
            </Button>
          </Group>
        </Stack>
      </Modal>
    </ResizableDrawer>
  )
}

export default CustomerDetail
